import React, { useState } from 'react';
import { Clock, Check, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { listTimezones, guessTimezone, timezoneLabel, formatInstant } from '../../utils/datetime';

/**
 * Timezone preference — DISPLAY only. The backend always stores UTC; this just controls how
 * timestamps (e.g. wallet activity) render for this user. Defaults to UTC until they pick one.
 * Persisted to the user's profile via the same client-write path as their display alias.
 */
const TimezoneSettings: React.FC = () => {
  const { user, updateTimezone } = useAuth();
  const current = user?.timezone || 'UTC';
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const zones = listTimezones();

  const save = async (tz: string) => {
    setValue(tz);
    setSaving(true); setSaved(false);
    try { await updateTimezone(tz); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    finally { setSaving(false); }
  };

  // Type-ahead: the full IANA list is hundreds of entries — too long for a dropdown. Filter as the admin types
  // (matching "New York" against "America/New_York" too, i.e. underscores as spaces). Bounded for render cost.
  const q = query.trim().toLowerCase().replace(/\s+/g, ' ');
  const matches = (q ? zones.filter((z) => z.toLowerCase().replace(/_/g, ' ').includes(q)) : zones).slice(0, 80);
  const pick = (tz: string) => { setQuery(''); setOpen(false); void save(tz); };

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
      <div className="flex items-center gap-2">
        <Clock className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Timezone</h3>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-secondary-400" />}
        {saved && !saving && <Check className="w-3.5 h-3.5 text-green-500" />}
      </div>
      <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
        Dates and times across the app (like your wallet activity) show in this timezone. We always store data in UTC — this only changes the display.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <input
            value={open ? query : value.replace(/_/g, ' ')}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => { setQuery(''); setOpen(true); }}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
            placeholder="Search timezone (e.g. New York, Tokyo)…" autoComplete="off"
            className="w-full rounded-lg border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-3 py-2 text-sm text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500"
          />
          {open && (
            <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 shadow-lg">
              {matches.length === 0 ? (
                <div className="px-3 py-2 text-xs text-secondary-400">No matching timezone</div>
              ) : matches.map((z) => (
                <button key={z} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(z)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary-50 dark:hover:bg-secondary-800 ${z === value ? 'font-semibold text-secondary-900 dark:text-white' : 'text-secondary-700 dark:text-secondary-200'}`}>
                  <span className="truncate">{z.replace(/_/g, ' ')}</span>
                  <span className="shrink-0 text-[11px] text-secondary-400">{timezoneLabel(z)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {value !== guessTimezone() && (
          <button onClick={() => save(guessTimezone())} className="text-xs text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200 underline">
            Use my device timezone ({guessTimezone()})
          </button>
        )}
      </div>

      <p className="mt-3 text-[11px] text-secondary-400">
        Current time here: <span className="tabular-nums">{formatInstant(new Date().toISOString(), value)}</span> ({timezoneLabel(value)})
      </p>
    </div>
  );
};

export default TimezoneSettings;
